import { Box, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import OptionsMenuIcon from '../../../core-components/assets/icons/OptionMenu';
import { BatchConfigRow } from '../types/BatchConfigType';
import DeleteBatchConfigModal from '../Dialogs/DeleteBatchConfigModal';
import TableActions from '../../../components/TableActions';
import type { RowActionAccess } from '../../../components/TableActions';
import AttachmentIcon from '@mui/icons-material/Attachment';
import { getBatchConfigTypeIconAndColor, getChipForDetectionType } from './helper';
import { Neutrals } from '../../../core-components/theme/color-palette/colors';



export const BatchConfigTableColumns: any = (
  navigate: ReturnType<typeof useNavigate>,
  t: TFunction,
  rowAccess: RowActionAccess
) => [
  {
    field: 'name',
    headerName: t('batchConfig.tableColumns.nameAndPurpose', 'Name & Purpose'),
    align: 'left',
    flex: 1,
    minWidth: 400,
    maxWidth: 700,

    renderCell: (_: string, row: BatchConfigRow) => {
      return (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
            title={row.name || t('batchConfig.tableColumns.noName', 'No Name')}
          >
            <Typography
              variant="body1"
              fontWeight="500"
              sx={{
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: {
                  xs: 'block',
                  sm: '-webkit-box',
                },
                WebkitLineClamp: {
                  xs: 'none',
                  sm: 1,
                },
              }}
            >
              {row.name || t('batchConfig.tableColumns.noName', 'No Name')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: {
                xs: 'block',
                sm: '-webkit-box',
              },
              WebkitLineClamp: {
                xs: 'none',
                sm: 2,
              },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            title={row.purpose || t('batchConfig.tableColumns.noPurpose', 'No purpose')}
          >
            <Typography variant="body2" sx={{ color: '#606060' }}>
              {row.purpose || t('batchConfig.tableColumns.noPurpose', 'No purpose')}
            </Typography>
          </Box>
        </Box>
      );
    },
  },

   {
    field: 'batchType',
    headerName: t('batchConfig.tableColumns.batchType', 'Batch Type'),
    align: 'left',
    flex: 1,
    minWidth: 100,
    renderCell: (value: string, row: BatchConfigRow) => {
      const {iconClass , color } = getBatchConfigTypeIconAndColor(row.batchType || '');
      return (
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
      <Typography variant="body2" sx={{ color: color }}>
        {iconClass}
        </Typography>
        <Typography variant="body2" sx={{ color: color }}>
        {row.batchType || t('batchConfig.tableColumns.noType', 'No Type')}
        </Typography>
        </Box>
      );
    },
  },

  {
    field: 'detectionType',
    headerName: t('batchConfig.tableColumns.detectionType', 'Detection Type'),
    align: 'left',
    flex: 1,
    minWidth: 100,
    renderCell: (value: string, row: BatchConfigRow) => {

      return (
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            {getChipForDetectionType(row?.detectionType || '')}
        </Box>
      );
    },
  },
  {
    field: 'attachedConditions',
    headerName: t('batchConfig.tableColumns.attachedConditions', 'Attached Conditions'),
    align: 'center',
    flex: 1,
    width: 250,
    renderCell: (_: unknown, row: BatchConfigRow) => {
        return (
          <Box>
                <Chip
                  icon={<AttachmentIcon />}
                  key={row?.attachedConditions}
                  label={row?.attachedConditions || t('common.notAvailableShort', 'N/A')}
                  sx={{
                    color: Neutrals[600],
                    backgroundColor: 'white',
                    border: `1px solid ${Neutrals[100]}`,
                    height: 24,
                  }}
                />
          </Box>
        );
      },
  },
  {
    field: 'action',
    headerName: (
      <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'end' }}>
        <OptionsMenuIcon />
      </Box>
    ),
    align: 'right',
    getActions: (row: BatchConfigRow) => (
      <TableActions
        rowData={row}
        DeleteComponent={DeleteBatchConfigModal}
        canEdit={rowAccess.canEdit}
        canDelete={rowAccess.canDelete}
        editAction={() =>  navigate(`createEditBatchConfig/${row?.batchId}`)}
      />
    ),
    width: 80,
  },
];
